import { useMemo } from 'react'

import { useBrandsQueries } from 'hooks-queries/brands'

import { GET_BRAND_TERRITORY_ERROR_LOG } from './use-brand-territories.consts'
import type {
  TBrandTerritories,
  IUseBrandTerritoriesProps,
  IUseBrandTerritoriesReturn,
} from './use-brand-territories.types'
import type { IPartialWorkspaceBrand } from '../../brands.types'

export const useBrandTerritories = ({
  currentTerritoryId,
  brandContentType,
  skipQuery,
  onCompleted,
  onError,
}: IUseBrandTerritoriesProps): IUseBrandTerritoriesReturn => {
  const { data, called, error, loading, refetch } = useBrandsQueries({
    queryName: 'getBrandTerritory',
    queryVariables: { territoryId: currentTerritoryId },
    errorLog: GET_BRAND_TERRITORY_ERROR_LOG,
    options: {
      skip: skipQuery || !brandContentType,
      onCompleted,
      onError,
    },
  })

  const currentBrand = useMemo((): IPartialWorkspaceBrand | undefined => {
    if (!data?.getTerritory) return undefined

    const urlTerritoryId = data.getTerritory.territoryId

    const workspaceBrand = data?.getWorkspace?.workspaceBrand
    const competitors = data?.getWorkspace?.workspaceBrandCompetitorsPaginated?.data ?? []
    const allBrands = [workspaceBrand, ...competitors].filter(Boolean)

    const foundBrand = allBrands.find(brand => {
      const territories = brand?.workspaceBrandTerritories
      if (!territories) return false

      const territoryIds = [
        territories.groupedTerritory?.territoryId,
        territories.selfContentTerritory?.territoryId,
        territories.earnedTerritory?.territoryId,
        territories.paidTerritory?.territoryId,
      ]
      return territoryIds.includes(urlTerritoryId)
    })

    if (!foundBrand) return undefined

    return {
      id: foundBrand.id,
      thumbnail: foundBrand.thumbnail,
      customSettings: foundBrand.customSettings,
      workspaceBrandTerritories: foundBrand.workspaceBrandTerritories,
    }
  }, [data])

  const territories = useMemo((): TBrandTerritories | null => {
    if (!currentBrand?.workspaceBrandTerritories) return null

    const brandTerritories = currentBrand.workspaceBrandTerritories

    return {
      total: brandTerritories.groupedTerritory?.territoryId ?? null,
      owned: brandTerritories.selfContentTerritory?.territoryId ?? null,
      earned: brandTerritories.earnedTerritory?.territoryId ?? null,
      paid: brandTerritories.paidTerritory?.territoryId ?? null,
    }
  }, [currentBrand])

  const creators = useMemo(() => {
    const territory = data?.getTerritory
    if (territory?.__typename !== 'BrandTerritory') return []
    return territory.territoryCreators?.clusters?.flatMap(cluster => cluster.creators?.topCreators ?? []) ?? []
  }, [data])

  return {
    territories,
    currentBrand,
    creators,
    data,
    loading,
    error,
    called,
    refetch,
  }
}
